<template>
  <div class="container max-w-6xl mx-auto px-4 py-8">
    <h1 class="text-3xl font-bold text-center mb-8 text-gray-800">EMDEU</h1>
    
    <!-- Секция с активными видеозвонками от медсестер -->
    <div v-if="activeVideoCalls.length > 0" class="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 mb-6 text-white">
      <div class="flex items-center mb-4">
        <div class="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
          <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
          </svg>
        </div>
        <div>
          <h2 class="text-xl font-bold">🔔 Входящий видеозвонок!</h2>
          <p class="text-green-100 text-sm">Медсестра ожидает видеозвонка с вами</p>
        </div>
      </div>
      
      <div class="space-y-3">
        <div v-for="call in activeVideoCalls" :key="call.id" class="bg-white bg-opacity-10 rounded-lg p-4">
          <div class="flex justify-between items-center">
            <div>
              <p class="font-semibold">Медсестра: {{ call.nurse_name }}</p>
              <p class="text-green-100 text-sm">Комната: {{ call.room_name }}</p>
              <p class="text-green-100 text-sm">Создано: {{ formatDateTime(call.created_at) }}</p>
            </div>
            <div class="flex space-x-2">
              <button 
                @click="joinNurseVideoCall(call)"
                class="px-4 py-2 bg-white text-green-600 rounded-lg hover:bg-green-50 font-semibold transition-colors"
              >
                🎥 Присоединиться
              </button>
              <button 
                @click="dismissVideoCall(call)"
                class="px-3 py-2 bg-red-500 bg-opacity-70 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <h2 class="text-xl font-semibold mb-4">Добро пожаловать!</h2>
      <p class="text-gray-700 mb-4">
        Вы успешно вошли в систему. Здесь будет размещена основная функциональность платформы.
      </p>
      
      <div class="grid md:grid-cols-2 gap-6 mt-8">
        <div class="border rounded-lg p-6 hover:shadow-md transition-shadow">
          <h3 class="text-lg font-medium mb-2">Для пациентов</h3>
          <p class="text-gray-600 mb-4">
            Записывайтесь на прием к врачу, отслеживайте свою медицинскую историю и 
            получайте рекомендации по здоровью.
          </p>
          <div class="flex flex-wrap gap-2">
            <button class="btn-primary">Записаться на прием</button>
            <router-link to="/health-tracking" class="btn-secondary">
              Трекер здоровья
            </router-link>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Секция с врачами участка пациента -->
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <h2 class="text-xl font-semibold mb-4">Врачи вашей поликлиники</h2>
      
      <div v-if="doctorsLoading" class="flex justify-center p-6">
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
      
      <div v-else-if="doctorsError" class="p-4 bg-red-50 text-red-600 rounded-md">
        {{ doctorsError }}
      </div>
      
      <div v-else>
        <div v-if="clinicDoctors.length > 0" class="overflow-x-auto">
          <table class="min-w-full bg-white">
            <thead>
              <tr class="bg-gray-100 text-gray-700">
                <th class="py-3 px-4 text-left">ФИО</th>
                <th class="py-3 px-4 text-left">Специализация</th>
                <th class="py-3 px-4 text-left">Стаж (лет)</th>
                <th class="py-3 px-4 text-left">Пол</th>
                <th class="py-3 px-4 text-left">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="doctor in clinicDoctors" :key="doctor.id" class="border-b hover:bg-gray-50">
                <td class="py-3 px-4">{{ doctor.full_name }}</td>
                <td class="py-3 px-4">{{ formatDoctorSpecialization(doctor) || 'Не указана' }}</td>
                <td class="py-3 px-4">{{ doctor.experience_years }}</td>
                <td class="py-3 px-4 font-semibold" :class="doctor.gender === 'M' ? 'text-blue-600' : 'text-red-600'">
                  {{ doctor.gender === 'M' ? 'М' : 'Ж' }}
                </td>
                <td class="py-3 px-4">
                  <button 
                    class="text-blue-600 hover:text-blue-800 mr-2"
                    @click="openBookingDialog(doctor)"
                  >
                    Записаться
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div v-else class="text-center p-4 text-gray-600">
          В вашем поликлинике пока нет врачей.
        </div>
      </div>
    </div>
    
    <!-- Секция с приемами пациента -->
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-gray-800">Мои записи на прием</h2>
        
        <div class="flex space-x-2">
          <button 
            @click="activePatientAppointmentsTab = 'upcoming'"
            class="px-3 py-1 text-sm rounded-md transition-colors"
            :class="activePatientAppointmentsTab === 'upcoming' ? 
              'bg-blue-100 text-blue-700' : 
              'text-gray-600 hover:bg-gray-100'"
          >
            Предстоящие
          </button>
          <button 
            @click="activePatientAppointmentsTab = 'today'"
            class="px-3 py-1 text-sm rounded-md transition-colors"
            :class="activePatientAppointmentsTab === 'today' ? 
              'bg-blue-100 text-blue-700' : 
              'text-gray-600 hover:bg-gray-100'"
          >
            Сегодня
          </button>
          <button 
            @click="activePatientAppointmentsTab = 'past'"
            class="px-3 py-1 text-sm rounded-md transition-colors"
            :class="activePatientAppointmentsTab === 'past' ? 
              'bg-blue-100 text-blue-700' : 
              'text-gray-600 hover:bg-gray-100'"
          >
            Прошедшие
          </button>
        </div>
      </div>
      
      <div v-if="patientAppointmentsLoading" class="flex justify-center p-6">
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
      
      <div v-else-if="patientAppointmentsError" class="p-4 bg-red-50 text-red-600 rounded-md">
        {{ patientAppointmentsError }}
      </div>
      
      <div v-else>
        <div v-if="patientAppointments.length > 0" class="overflow-x-auto">
          <table class="min-w-full bg-white">
            <thead>
              <tr class="bg-gray-100 text-gray-700">
                <th class="py-3 px-4 text-left">Тип / Провайдер</th>
                <th class="py-3 px-4 text-left">Дата</th>
                <th class="py-3 px-4 text-left">Время</th>
                <th class="py-3 px-4 text-left">Причина визита</th>
                <th class="py-3 px-4 text-left">Статус</th>
                <th class="py-3 px-4 text-left">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="appointment in patientAppointments" :key="appointment.id" class="border-b hover:bg-gray-50">
                <td class="py-3 px-4">
                  <div class="flex items-center">
                    <div class="w-8 h-8 rounded-full flex items-center justify-center mr-2"
                         :class="appointment.call_type === 'nurse_call' ? 'bg-green-100' : 'bg-blue-100'">
                      <svg v-if="appointment.call_type === 'nurse_call'" class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.5 6c-2.61.7-5.67 1-8.5 1s-5.89-.3-8.5-1L3 8c0 .55.45 1 1 1h1v10c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-3h6v3c0 .55.45 1 1 1h2c.55 0 1-.45 1-1V9h1c.55 0 1-.45 1-1l-.5-2zM12 6c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/>
                      </svg>
                      <svg v-else class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                      </svg>
                    </div>
                    <div>
                      <div class="font-medium">{{ getProviderName(appointment) }}</div>
                      <div class="text-xs text-gray-500">
                        {{ appointment.call_type === 'nurse_call' ? 'Звонок медсестры' : 'Прием к врачу' }}
                        <span v-if="appointment.call_type === 'nurse_call' && appointment.duration_minutes">
                          · {{ appointment.duration_minutes }} мин
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="py-3 px-4">{{ formatDate(getAppointmentDate(appointment)) }}</td>
                <td class="py-3 px-4">{{ formatTime(getAppointmentStartTime(appointment)) }} - {{ formatTime(getAppointmentEndTime(appointment)) }}</td>
                <td class="py-3 px-4">
                  <div>{{ appointment.reason_display || appointment.reason?.name || 'Не указана' }}</div>
                  <div v-if="appointment.call_type === 'nurse_call' && appointment.notes" class="text-xs text-gray-500 mt-1">
                    {{ appointment.notes }}
                  </div>
                </td>
                <td class="py-3 px-4">
                  <span 
                    class="px-2 py-1 text-xs rounded-full" 
                    :class="getStatusClass(appointment.status)"
                  >
                    {{ getStatusText(appointment.status) }}
                  </span>
                </td>
                <td class="py-3 px-4">
                  <button 
                    v-if="canPatientCancelAppointment(appointment)"
                    class="px-3 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 mr-1"
                    @click="confirmCancelAppointment(appointment)"
                  >
                    Отменить
                  </button>
                  <button 
                    v-if="appointment.video_room_url"
                    class="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    @click="joinVideoConsultation(appointment.video_room_url)"
                  >
                    Войти в видеоприем
                  </button>
                  <button 
                    v-if="appointment.status === 'completed' && appointment.video_room_name"
                    class="px-3 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 ml-1"
                    @click="showRoomRecords(appointment)"
                  >
                    Записи встречи
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div v-else class="text-center p-4 text-gray-600">
          <p v-if="activePatientAppointmentsTab === 'upcoming'">У вас нет предстоящих приемов.</p>
          <p v-else-if="activePatientAppointmentsTab === 'today'">У вас нет приемов на сегодня.</p>
          <p v-else>У вас нет прошедших приемов.</p>
        </div>
      </div>
    </div>
    
    <!-- Appointment Booking Dialog -->
    <AppointmentBooking 
      :is-open="isBookingDialogOpen"
      :doctor="selectedDoctor"
      @close="closeBookingDialog"
      @appointment-created="handleAppointmentCreated"
    />
    
    <!-- Cancellation Confirmation Dialog -->
    <div v-if="showCancellationDialog" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- Backdrop overlay -->
      <div class="fixed inset-0 bg-black opacity-50" @click="closeCancellationDialog"></div>
      
      <!-- Modal dialog -->
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto z-10">
          <div class="px-6 py-4 border-b">
            <h2 class="text-xl font-semibold text-gray-800">Отмена приема</h2>
          </div>
          
          <div class="p-6">
            <p class="mb-4 text-gray-700">
              Вы уверены, что хотите отменить этот прием?
            </p>
            
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Причина отмены (необязательно)</label>
              <textarea 
                v-model="cancellationReason"
                class="form-textarea w-full rounded-md border-gray-300 shadow-sm"
                rows="3"
                placeholder="Укажите причину отмены приема"
              ></textarea>
            </div>
          </div>
          
          <div class="px-6 py-3 border-t bg-gray-50 flex justify-between rounded-b-lg">
            <button 
              @click="closeCancellationDialog"
              class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Отмена
            </button>
            
            <button 
              @click="cancelAppointment"
              class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              :disabled="cancellationLoading"
              :class="{'opacity-50 cursor-not-allowed': cancellationLoading}"
            >
              {{ cancellationLoading ? 'Отменяем...' : 'Подтвердить отмену' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Room Records Dialog -->
    <div v-if="showRoomRecordsDialog" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- Backdrop overlay -->
      <div class="fixed inset-0 bg-black opacity-50" @click="closeRoomRecordsDialog"></div>
      
      <!-- Modal dialog -->
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-auto z-10">
          <div class="px-6 py-4 border-b">
            <h2 class="text-xl font-semibold text-gray-800">Записи встречи</h2>
            <p v-if="selectedAppointmentForRecords" class="text-sm text-gray-600 mt-1">
              Комната: {{ selectedAppointmentForRecords.video_room_name }}
            </p>
          </div>
          
          <div class="p-6">
            <div v-if="roomRecordsLoading" class="flex justify-center p-6">
              <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
              <span class="ml-2">Загружаем записи...</span>
            </div>
            
            <div v-else-if="roomRecordsError" class="text-center p-8">
              <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Ошибка загрузки</h3>
              <p class="text-red-600 mb-4">{{ roomRecordsError }}</p>
              <button 
                @click="showRoomRecords(selectedAppointmentForRecords)"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Попробовать снова
              </button>
            </div>
            
            <div v-else-if="roomRecords.length > 0">
              <p class="mb-4 text-gray-700">
                Найдено записей: {{ roomRecords.length }}
              </p>
              
              <div class="space-y-3">
                <div 
                  v-for="(record, index) in roomRecords" 
                  :key="record" 
                  class="border rounded-lg p-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div class="flex items-center">
                    <div class="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                      <svg class="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 class="font-medium text-gray-900">Запись {{ index + 1 }}</h4>
                      <p class="text-sm text-gray-500">Видеофайл встречи</p>
                    </div>
                  </div>
                  
                  <div class="flex space-x-2">
                    <a 
                      :href="record"
                      target="_blank"
                      class="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Просмотреть
                    </a>
                    <a 
                      :href="record"
                      :download="`record-${index + 1}.mp4`"
                      class="px-3 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Скачать
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-else class="text-center p-8">
              <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Записей не найдено</h3>
              <p class="text-gray-600">Для этой встречи пока нет доступных записей.</p>
            </div>
          </div>
          
          <div class="px-6 py-3 border-t bg-gray-50 flex justify-end rounded-b-lg">
            <button 
              @click="closeRoomRecordsDialog"
              class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Секция с историей звонков медсестер -->
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-gray-800">История звонков медсестер</h2>
        
        <div class="flex items-center space-x-4">
          <!-- Search bar -->
          <div class="relative">
            <input 
              v-model="nurseCallsSearchQuery"
              type="text" 
              placeholder="Поиск по медсестре..."
              class="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500 text-sm w-64"
            >
            <svg class="w-4 h-4 text-gray-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          
          <!-- Period filter -->
          <select v-model="selectedNurseCallsPeriod" class="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-green-500 focus:border-green-500">
            <option value="all">Все время</option>
            <option value="today">Сегодня</option>
            <option value="week">Эта неделя</option>
            <option value="month">Этот месяц</option>
          </select>
        </div>
      </div>
      
      <div v-if="nurseCallsLoading" class="flex justify-center p-6">
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-600"></div>
        <span class="ml-2">Загружаем историю звонков...</span>
      </div>
      
      <div v-else-if="nurseCallsError" class="p-4 bg-red-50 text-red-600 rounded-md">
        {{ nurseCallsError }}
        <button @click="fetchPatientNurseCalls" class="ml-4 px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm">
          Попробовать снова
        </button>
      </div>
      
      <div v-else>
        <div v-if="filteredNurseCalls.length > 0" class="overflow-x-auto">
          <table class="min-w-full bg-white">
            <thead>
              <tr class="bg-gray-50 text-gray-700">
                <th class="py-3 px-4 text-left font-medium">Медсестра</th>
                <th class="py-3 px-4 text-left font-medium">Дата</th>
                <th class="py-3 px-4 text-left font-medium">Время</th>
                <th class="py-3 px-4 text-left font-medium">Длительность</th>
                <th class="py-3 px-4 text-left font-medium">Заметки</th>
                <th class="py-3 px-4 text-left font-medium">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="call in filteredNurseCalls" :key="call.id" class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td class="py-3 px-4">
                  <div class="flex items-center">
                    <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-2">
                      <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.5 6c-2.61.7-5.67 1-8.5 1s-5.89-.3-8.5-1L3 8c0 .55.45 1 1 1h1v10c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-3h6v3c0 .55.45 1 1 1h2c.55 0 1-.45 1-1V9h1c.55 0 1-.45 1-1l-.5-2zM12 6c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1z"/>
                      </svg>
                    </div>
                    <div>{{ getProviderName(call) }}</div>
                  </div>
                </td>
                <td class="py-3 px-4 text-gray-600">{{ formatDate(getAppointmentDate(call)) }}</td>
                <td class="py-3 px-4 text-gray-600">{{ formatTime(getAppointmentStartTime(call)) }}</td>
                <td class="py-3 px-4 text-gray-600">
                  <span v-if="call.duration_minutes" class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    {{ formatDuration(call.duration_minutes) }}
                  </span>
                  <span v-else class="text-gray-400 text-sm">Не указана</span>
                </td>
                <td class="py-3 px-4 text-gray-600">{{ call.notes || 'Нет заметок' }}</td>
                <td class="py-3 px-4">
                  <div class="flex space-x-2">
                    <button 
                      @click="viewNurseCallRecords(call)"
                      class="px-3 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                      :disabled="nurseCallRecordsLoading[call.id]"
                    >
                      <span v-if="nurseCallRecordsLoading[call.id]">Загрузка...</span>
                      <span v-else>Записи</span>
                    </button>
                    <button 
                      v-if="call.status === 'in_progress' && call.video_room_url"
                      @click="joinVideoConsultation(call.video_room_url)"
                      class="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Войти
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div v-else class="text-center p-4 text-gray-600">
          <p v-if="nurseCallsSearchQuery">Звонки не найдены по запросу "{{ nurseCallsSearchQuery }}"</p>
          <p v-else-if="selectedNurseCallsPeriod !== 'all'">Нет звонков за выбранный период</p>
          <p v-else>У вас пока нет истории звонков с медсестрами</p>
        </div>
      </div>
    </div>

    <!-- Nurse Call Records Modal -->
    <div v-if="showNurseCallRecordsModal" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- Backdrop overlay -->
      <div class="fixed inset-0 bg-black opacity-50" @click="closeNurseCallRecordsModal"></div>
      
      <!-- Modal dialog -->
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-auto z-10">
          <div class="px-6 py-4 border-b">
            <h2 class="text-xl font-semibold text-gray-800">Записи звонка медсестры</h2>
            <p v-if="selectedNurseCall" class="text-sm text-gray-600 mt-1">
              Комната: {{ selectedNurseCall.video_room_name }}
            </p>
          </div>
          
          <div class="p-6">
            <div v-if="nurseCallRecordsLoading[selectedNurseCall?.id]" class="flex justify-center p-6">
              <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-600"></div>
              <span class="ml-2">Загружаем записи...</span>
            </div>
            
            <div v-else-if="nurseCallRecordsError" class="text-center p-8">
              <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Ошибка загрузки</h3>
              <p class="text-red-600 mb-4">{{ nurseCallRecordsError }}</p>
              <button 
                @click="viewNurseCallRecords(selectedNurseCall)"
                class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Попробовать снова
              </button>
            </div>
            
            <div v-else-if="nurseCallRecords.length > 0">
              <p class="mb-4 text-gray-700">
                Найдено записей: {{ nurseCallRecords.length }}
              </p>
              
              <div class="space-y-3">
                <div 
                  v-for="(record, index) in nurseCallRecords" 
                  :key="record" 
                  class="border rounded-lg p-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div class="flex items-center">
                    <div class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <svg class="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path>
                      </svg>
                    </div>
                    <div>
                      <h4 class="font-medium text-gray-900">Запись {{ index + 1 }}</h4>
                      <p class="text-sm text-gray-500">Видеофайл звонка</p>
                    </div>
                  </div>
                  
                  <div class="flex space-x-2">
                    <a 
                      :href="record"
                      target="_blank"
                      class="px-3 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Просмотреть
                    </a>
                    <a 
                      :href="record"
                      :download="`nurse-call-record-${index + 1}.mp4`"
                      class="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Скачать
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-else class="text-center p-8">
              <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">Записей не найдено</h3>
              <p class="text-gray-600">Для этого звонка пока нет доступных записей.</p>
            </div>
          </div>
          
          <div class="px-6 py-3 border-t bg-gray-50 flex justify-end rounded-b-lg">
            <button 
              @click="closeNurseCallRecordsModal"
              class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api'
import AppointmentBooking from '../components/AppointmentBooking.vue'
import { formatDoctorSpecialization as formatDoctorSpecializationText } from '../utils/doctorDisplay'

export default {
  name: 'PatientHome',
  components: {
    AppointmentBooking
  },
  data() {
    return {
      clinicDoctors: [],
      doctorsLoading: false,
      doctorsError: null,
      isBookingDialogOpen: false,
      selectedDoctor: null,
      activePatientAppointmentsTab: 'upcoming',
      patientAppointments: [],
      patientAppointmentsLoading: false,
      patientAppointmentsError: null,
      doctorsData: {},
      nursesData: {},
      slotsData: {},
      // Cancellation dialog
      showCancellationDialog: false,
      appointmentToCancel: null,
      cancellationReason: '',
      cancellationLoading: false,
      // Room records dialog
      showRoomRecordsDialog: false,
      roomRecordsLoading: false,
      roomRecords: [],
      selectedAppointmentForRecords: null,
      roomRecordsError: null,
      // Active video calls from nurses
      activeVideoCalls: [],
      videoCallsLoading: false,
      pollingInterval: null,
      
      // Для истории звонков медсестер
      nurseCalls: [],
      nurseCallsLoading: false,
      nurseCallsError: null,
      nurseCallsSearchQuery: '',
      selectedNurseCallsPeriod: 'all',
      
      // Для модального окна записей звонков
      showNurseCallRecordsModal: false,
      selectedNurseCall: null,
      nurseCallRecords: [],
      nurseCallRecordsLoading: {},
      nurseCallRecordsError: null,
    }
  },
  created() {
          this.fetchClinicDoctors();
    this.fetchPatientAppointments();
    this.fetchActiveVideoCalls();
    this.fetchPatientNurseCalls();
    this.startVideoCallsPolling();
  },
  beforeUnmount() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  },
  watch: {
    activePatientAppointmentsTab() {
      this.fetchPatientAppointments();
    }
  },
  computed: {
    filteredNurseCalls() {
      let filtered = this.nurseCalls

      // Фильтр по поиску
      if (this.nurseCallsSearchQuery) {
        const query = this.nurseCallsSearchQuery.toLowerCase()
        filtered = filtered.filter(call => {
          const nurseName = this.getProviderName(call).toLowerCase()
          return nurseName.includes(query)
        })
      }

      // Фильтр по периоду
      if (this.selectedNurseCallsPeriod !== 'all') {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        
        filtered = filtered.filter(call => {
          const callDate = new Date(call.booked_at || call.created_at)
          
          switch (this.selectedNurseCallsPeriod) {
            case 'today':
              return callDate >= today
            case 'week': {
              const weekAgo = new Date(today)
              weekAgo.setDate(weekAgo.getDate() - 7)
              return callDate >= weekAgo
            }
            case 'month': {
              const monthAgo = new Date(today)
              monthAgo.setMonth(monthAgo.getMonth() - 1)
              return callDate >= monthAgo
            }
            default:
              return true
          }
        })
      }

      return filtered.sort((a, b) => new Date(b.booked_at || b.created_at) - new Date(a.booked_at || a.created_at))
    }
  },
  methods: {
    formatDoctorSpecialization(doctor) {
      return formatDoctorSpecializationText(doctor)
    },
    async fetchClinicDoctors() {
      try {
        this.doctorsLoading = true;
        const response = await api.getPatientClinicDoctors();
        this.clinicDoctors = response.data || [];
        this.doctorsError = null;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          if (error.response.data && error.response.data.detail) {
            this.doctorsError = error.response.data.detail;
          } else {
            this.doctorsError = 'Не удалось найти врачей для вашего участка.';
          }
        } else {
          this.doctorsError = 'Не удалось загрузить список врачей. Пожалуйста, попробуйте позже.';
        }
      } finally {
        this.doctorsLoading = false;
      }
    },
    
    openBookingDialog(doctor) {
      this.selectedDoctor = doctor;
      this.isBookingDialogOpen = true;
    },
    
    closeBookingDialog() {
      this.isBookingDialogOpen = false;
    },
    
    handleAppointmentCreated() {
      this.fetchPatientAppointments();
    },
    
    async fetchPatientAppointments() {
      try {
        this.patientAppointmentsLoading = true;
        const response = await api.getPatientAppointments({ status: this.activePatientAppointmentsTab });
        
        
        // Handle both paginated and non-paginated responses
        let appointmentsData = [];
        if (response.data && Array.isArray(response.data)) {
          appointmentsData = response.data;
        } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
          appointmentsData = response.data.results;
        } else {
      // No action needed
        }
        
        // Set the appointments directly
        this.patientAppointments = appointmentsData;
        
        // After setting appointments, we need to fetch additional data
        await this.fetchPatientAppointmentDetails();
        
        this.patientAppointmentsError = null;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          if (error.response.data && error.response.data.detail) {
            this.patientAppointmentsError = error.response.data.detail;
          } else {
            this.patientAppointmentsError = 'Не удалось загрузить список приемов. Пожалуйста, попробуйте позже.';
          }
        } else {
          this.patientAppointmentsError = 'Не удалось загрузить список приемов. Пожалуйста, попробуйте позже.';
        }
      } finally {
        this.patientAppointmentsLoading = false;
      }
    },
    
    async fetchPatientAppointmentDetails() {
      // Store slot, doctor and nurse data separately
      this.slotsData = {};
      this.doctorsData = {};
      this.nursesData = {};
      
      // Create a list of IDs to fetch
      const slotIds = this.patientAppointments.map(a => a.slot).filter(id => id);
      const doctorIds = this.patientAppointments.map(a => a.doctor).filter(id => id);
      const nurseIds = this.patientAppointments.map(a => a.nurse).filter(id => id);
      
      // Fetch slots
      if (slotIds.length > 0) {
        try {
          // Fetch each slot individually
          for (const slotId of slotIds) {
            try {
              const slotResponse = await api.getSlotById(slotId);
              if (slotResponse.data) {
                this.slotsData[slotId] = slotResponse.data;
              }
            } catch (slotError) {
              // Create a placeholder for failed slots
              this.slotsData[slotId] = {
                id: slotId,
                date: 'Дата неизвестна',
                start_time: 'Время неизвестно',
                end_time: 'Время неизвестно'
              };
            }
          }
          
        } catch (error) {
      // Error handled silently
        }
      }
      
      // Try to fetch doctor info if there's an endpoint for it
      if (doctorIds.length > 0) {
        try {
          for (const doctorId of doctorIds) {
            try {
              const response = await api.getDoctorById(doctorId);
              if (response.data) {
                this.doctorsData[doctorId] = response.data;
              }
            } catch (doctorError) {
              // Create a placeholder
              this.doctorsData[doctorId] = { id: doctorId };
            }
          }
        } catch (error) {
      // Error handled silently
        }
      }
      
      // Fetch nurse info if there are nurse appointments
      if (nurseIds.length > 0) {
        try {
          for (const nurseId of nurseIds) {
            try {
              const response = await api.getNurseById(nurseId);
              if (response.data) {
                this.nursesData[nurseId] = response.data;
              }
            } catch (nurseError) {
              // Create a placeholder
              this.nursesData[nurseId] = { id: nurseId, user: { full_name: 'Медсестра' } };
            }
          }
        } catch (error) {
      // Error handled silently
        }
      }
    },
    
    getDoctorName(appointment) {
      if (!appointment) return 'Неизвестный врач';
      
      const doctorId = appointment.doctor;
      if (!doctorId) return 'Неизвестный врач';
      
      // Check if we have detailed doctor data
      const doctorData = this.doctorsData[doctorId];
      if (doctorData) {
        if (doctorData.full_name) return doctorData.full_name;
        if (doctorData.user && doctorData.user.full_name) return doctorData.user.full_name;
        
        // Try to construct name from parts if available
        const firstName = doctorData.first_name || '';
        const lastName = doctorData.last_name || '';
        if (firstName || lastName) {
          return `${lastName} ${firstName}`.trim();
        }
      }
      
      // Fallback to doctor ID
      return `Врач #${doctorId}`;
    },
    
    // Новые методы для объединенной системы приемов
    getAppointmentDate(appointment) {
      if (appointment.call_type === 'nurse_call') {
        // Для звонков медсестер используем время создания
        return appointment.booked_at || appointment.created_at
      } else {
        // Для приемов врачей используем дату слота
        const slot = this.slotsData[appointment.slot]
        return slot ? slot.date : appointment.booked_at
      }
    },
    
    getAppointmentStartTime(appointment) {
      if (appointment.call_type === 'nurse_call') {
        const date = new Date(appointment.booked_at || appointment.created_at)
        return date.toTimeString().substring(0, 5)
      } else {
        const slot = this.slotsData[appointment.slot]
        return slot ? slot.start_time : ''
      }
    },
    
    getAppointmentEndTime(appointment) {
      if (appointment.call_type === 'nurse_call') {
        if (appointment.duration_minutes) {
          const start = new Date(appointment.booked_at || appointment.created_at)
          const end = new Date(start.getTime() + appointment.duration_minutes * 60000)
          return end.toTimeString().substring(0, 5)
        }
        return ''
      } else {
        const slot = this.slotsData[appointment.slot]
        return slot ? slot.end_time : ''
      }
    },
    
    getProviderName(appointment) {
      if (appointment.call_type === 'nurse_call' && appointment.nurse) {
        const nurse = this.nursesData[appointment.nurse]
        if (nurse && nurse.user) {
          return this.formatUserName(nurse.user)
        }
        return 'Медсестра'
      } else if (appointment.doctor) {
        const doctor = this.doctorsData[appointment.doctor]
        if (doctor && doctor.user) {
          return this.formatUserName(doctor.user)
        }
        return 'Врач'
      }
      return 'Неизвестно'
    },
    
    formatUserName(user) {
      const parts = []
      if (user.last_name) parts.push(user.last_name)
      if (user.first_name) parts.push(user.first_name)
      if (user.middle_name) parts.push(user.middle_name)
      return parts.join(' ') || user.full_name || 'Пользователь'
    },
    
    // Старые методы для совместимости
    getSlotDate(appointment) {
      return this.getAppointmentDate(appointment)
    },
    
    getSlotStartTime(appointment) {
      return this.getAppointmentStartTime(appointment)
    },
    
    getSlotEndTime(appointment) {
      return this.getAppointmentEndTime(appointment)
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU')
    },
    
    formatTime(timeString) {
      if (!timeString) return '';
      
      // If the string is already in format like "08:00:00"
      if (typeof timeString === 'string' && timeString.includes(':')) {
        return timeString.substring(0, 5); // Return first 5 characters (HH:MM)
      }
      
      // If it's a date object or date string
      try {
        const time = new Date(timeString);
        return time.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        return '';
      }
    },
    
    getStatusClass(status) {
      switch (status) {
        case 'scheduled':
          return 'bg-blue-100 text-blue-700'
        case 'in_progress':
          return 'bg-green-100 text-green-700'
        case 'completed':
          return 'bg-gray-100 text-gray-700'
        case 'cancelled':
          return 'bg-red-100 text-red-700'
        case 'no_show':
          return 'bg-orange-100 text-orange-700'
        case 'patient_no_show':
          return 'bg-orange-100 text-orange-700'
        default:
          return 'bg-gray-100 text-gray-700'
      }
    },
    
    getStatusText(status) {
      switch (status) {
        case 'scheduled':
          return this.$t('appointment.scheduled')
        case 'in_progress':
          return this.$t('appointment.inProgress')
        case 'completed':
          return this.$t('appointment.completed')
        case 'cancelled':
          return this.$t('appointment.cancelled')
        case 'no_show':
          return this.$t('appointment.no_show')
        case 'patient_no_show':
          return this.$t('appointment.patient_no_show')
        default:
          return this.$t('appointment.unknownStatus')
      }
    },
    
    canPatientCancelAppointment(appointment) {
      return appointment.status === 'scheduled' || appointment.status === 'in_progress'
    },
    
    confirmCancelAppointment(appointment) {
      this.appointmentToCancel = appointment;
      this.showCancellationDialog = true;
      this.cancellationReason = '';
    },
    
    async cancelAppointment() {
      if (!this.appointmentToCancel || this.cancellationLoading) return;
      
      this.cancellationLoading = true;
      
      try {
        // The API call to cancel the appointment
        await api.cancelAppointment(this.appointmentToCancel.id);
        
        // Update the status in the local list to avoid needing to refresh
        const index = this.patientAppointments.findIndex(a => a.id === this.appointmentToCancel.id);
        if (index !== -1) {
          this.patientAppointments[index].status = 'cancelled';
        }
        
        // Close the dialog
        this.closeCancellationDialog();
        
        // Optionally refresh the appointments list
        await this.fetchPatientAppointments();
      } catch (error) {
        // Handle error (you might want to show an error message to the user)
      } finally {
        this.cancellationLoading = false;
      }
    },
    
    closeCancellationDialog() {
      this.showCancellationDialog = false;
    },
    
    async joinVideoConsultation(videoRoomUrl) {
      try {
        // Redirect to the video consultation room
        window.open(videoRoomUrl, '_blank');
      } catch (error) {
        alert('Не удалось присоединиться к видеоприему. Пожалуйста, попробуйте позже.');
      }
    },
    
    async showRoomRecords(appointment) {
      try {
        if (!appointment.video_room_name) {
          alert('Название комнаты недоступно для этого приема.');
          return;
        }

        this.selectedAppointmentForRecords = appointment;
        this.showRoomRecordsDialog = true;
        this.roomRecordsLoading = true;
        this.roomRecords = [];
        this.roomRecordsError = null;

        
        const response = await api.getRoomRecords(appointment.video_room_name);
        
        
        if (response.data && response.data.result === 'success' && response.data.data) {
          this.roomRecords = response.data.data.record_urls || [];
          
          if (this.roomRecords.length > 0) {
          } else {
      // No action needed
          }
        } else if (response.data && response.data.result === 'error') {
          this.roomRecordsError = response.data.error || 'Ошибка получения записей';
          this.roomRecords = [];
        } else {
          this.roomRecords = [];
        }
      } catch (error) {
        this.roomRecordsError = 'Не удалось загрузить записи встречи. Возможно, записи еще не готовы.';
        this.roomRecords = [];
      } finally {
        this.roomRecordsLoading = false;
      }
    },
    
    closeRoomRecordsDialog() {
      this.showRoomRecordsDialog = false;
      this.selectedAppointmentForRecords = null;
      this.roomRecords = [];
      this.roomRecordsError = null;
    },
    
    // Video calls methods
    async fetchActiveVideoCalls() {
      try {
        this.videoCallsLoading = true;
        const response = await api.getPatientActiveVideoCalls();
        this.activeVideoCalls = response.data || [];
      } catch (error) {
        // Не показываем ошибку, просто скрываем секцию
        this.activeVideoCalls = [];
      } finally {
        this.videoCallsLoading = false;
      }
    },
    
    startVideoCallsPolling() {
      // Обновляем список активных звонков каждые 30 секунд
      this.pollingInterval = setInterval(() => {
        this.fetchActiveVideoCalls();
      }, 30000);
    },
    
    joinNurseVideoCall(call) {
      try {
        // Открываем комнату в новой вкладке
        window.open(call.room_url, '_blank');
        
        // Убираем уведомление после присоединения
        this.dismissVideoCall(call);
        
        // Обновляем историю звонков
        this.fetchPatientNurseCalls();
      } catch (error) {
        alert('Не удалось присоединиться к видеозвонку. Пожалуйста, попробуйте позже.');
      }
    },
    
    dismissVideoCall(call) {
      // Убираем звонок из списка активных
      this.activeVideoCalls = this.activeVideoCalls.filter(c => c.id !== call.id);
    },
    
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '';
      
      try {
        const date = new Date(dateTimeString);
        return date.toLocaleString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return '';
      }
    },
    
    async fetchPatientNurseCalls() {
      try {
        this.nurseCallsLoading = true;
        this.nurseCallsError = null;
        
        // Получаем только звонки от медсестер
        const response = await api.getPatientAppointments({ call_type: 'nurse_call' });
        
        // Handle both paginated and non-paginated responses
        let nurseCalls = [];
        if (response.data && Array.isArray(response.data)) {
          nurseCalls = response.data;
        } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
          nurseCalls = response.data.results;
        }
        
        this.nurseCalls = nurseCalls;
        
        // Загрузим дополнительные данные по медсестрам
        await this.fetchPatientAppointmentDetails();
      } catch (error) {
        this.nurseCallsError = 'Не удалось загрузить историю звонков медсестер';
      } finally {
        this.nurseCallsLoading = false;
      }
    },
    
    async viewNurseCallRecords(call) {
      try {
        if (!call.video_room_name) {
          alert('Название комнаты недоступно для этого звонка.');
          return;
        }

        this.selectedNurseCall = call;
        this.showNurseCallRecordsModal = true;
        this.nurseCallRecordsLoading = { ...this.nurseCallRecordsLoading, [call.id]: true };
        this.nurseCallRecords = [];
        this.nurseCallRecordsError = null;

        
        const response = await api.getRoomRecords(call.video_room_name);
        
        
        if (response.data && response.data.result === 'success' && response.data.data) {
          this.nurseCallRecords = response.data.data.record_urls || [];
        } else if (response.data && response.data.result === 'error') {
          this.nurseCallRecordsError = response.data.error || 'Ошибка получения записей';
          this.nurseCallRecords = [];
        } else {
          this.nurseCallRecords = [];
        }
      } catch (error) {
        this.nurseCallRecordsError = 'Не удалось загрузить записи звонка. Возможно, записи еще не готовы.';
        this.nurseCallRecords = [];
      } finally {
        this.nurseCallRecordsLoading = { ...this.nurseCallRecordsLoading, [call.id]: false };
      }
    },
    
    closeNurseCallRecordsModal() {
      this.showNurseCallRecordsModal = false;
      this.selectedNurseCall = null;
      this.nurseCallRecords = [];
      this.nurseCallRecordsError = null;
    },
    
    formatDuration(minutes) {
      if (!minutes) return 'Неизвестно';
      
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      
      if (hours > 0) {
        return `${hours}ч ${mins}м`;
      } else {
        return `${mins}м`;
      }
    },
  }
}
</script>

<style scoped>
.btn-primary {
  @apply px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors;
}

.btn-secondary {
  @apply px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors;
}
</style> 
